import { ChangeDetectorRef, Component, Inject } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AutocompleteComponent } from '../autocomplete/autocomplete.component';
import { tipologicaInterface } from '../../../interfaces/tipologica.interface';
import { DateConversionService } from '../../utilities/dateconversion.service';
import { Rettifica } from '../../../interfaces/rettifica.interface';
import { Subscription, switchMap } from 'rxjs';
import { RettificheService } from '../../../service/rettifiche.service';
import { LoaderService } from '../../../service/loader.service';
import { RequireMatch } from '../autocomplete/requireMatch';
import { CommonModule, DatePipe } from '@angular/common';
import { TipologicheService } from '../../../service/tipologiche.service';
import { ModificaModalData } from '../../../interfaces/modal.interface';
import { DragDropModule } from '@angular/cdk/drag-drop';

@Component({
  selector: 'app-modifica-modal',
  standalone: true,
  imports: [ReactiveFormsModule, AutocompleteComponent, CommonModule, DragDropModule],
  templateUrl: './modifica-modal.component.html',
})
export class ModificaModalComponent {
  commesse: tipologicaInterface[] = [];
  filteredCommesse: tipologicaInterface[] = [];
  isCommessaTariffa = false;
  interventiList: tipologicaInterface[] = [];
  ruoliList: tipologicaInterface[] = [];

  form: FormGroup = this.fb.group({
    id: [0],
    dataDa: [null, Validators.required],
    dataA: [null, Validators.required],
    codiceCommessaSorgente: [null, [Validators.required, RequireMatch]],
    codiceCommessaDestinazione: [null, [Validators.required, RequireMatch]],
    intervento: [null, [Validators.required, RequireMatch]],
    ruolo: [{ value: null, disabled: true }],
  });

  subscriptions = new Subscription();

  commesseErrate: tipologicaInterface[] = [];
  filteredCommesseErrate: tipologicaInterface[] = [];
  modificaModalData: ModificaModalData;

  inizioMeseTimesheet = '';
  fineMeseTimesheet = '';
  matricola = '';

  get id(): FormControl<number> {
    return this.form.get('id') as FormControl;
  }

  get dataDa(): FormControl<string> {
    return this.form.get('dataDa') as FormControl;
  }

  get dataA(): FormControl<string> {
    return this.form.get('dataA') as FormControl;
  }

  get codiceCommessaSorgente(): FormControl<tipologicaInterface | null> {
    return this.form.get('codiceCommessaSorgente') as FormControl;
  }

  get codiceCommessaDestinazione(): FormControl<tipologicaInterface | null> {
    return this.form.get('codiceCommessaDestinazione') as FormControl;
  }

  get intervento(): FormControl<tipologicaInterface | null> {
    return this.form.get('intervento') as FormControl;
  }

  get ruolo(): FormControl {
    return this.form.get('ruolo') as FormControl;
  }

  constructor(
    private fb: FormBuilder,
    private dateConversionService: DateConversionService,
    private dialogRef: MatDialogRef<ModificaModalComponent>,
    private rettificheService: RettificheService,
    private loaderService: LoaderService,
    private tipologicheService: TipologicheService,
    private cd: ChangeDetectorRef,
    @Inject(MAT_DIALOG_DATA) public data: ModificaModalData,
    datePipe: DatePipe
  ) {
    if (data.anno && data.mese) {
      this.inizioMeseTimesheet = `${data.anno}-${data.periodo}-01`;

      const lastDayOfTheMonthDate = new Date(data.anno, data.mese, 0);
      this.fineMeseTimesheet =
        datePipe.transform(lastDayOfTheMonthDate, 'yyyy-MM-dd') ?? '';
    }
    this.commesse = data.commesse ?? [];
    this.commesseErrate = data.commesseErrate ?? [];
    this.matricola = data.matricola ?? '';
    this.modificaModalData = data;
  }

  ngOnInit(): void {
    this.onValueChanges();
  }

  ngAfterViewInit(): void {
    if (this.modificaModalData.rettifica?.id) {
      this.id.patchValue(this.modificaModalData.rettifica?.id, {
        emitEvent: false,
      });
    }
    if (this.modificaModalData.rettifica?.dataA) {
      this.dataA.patchValue(
        this.dateConversionService.convertDateToISO(this.modificaModalData.rettifica?.dataA),
        { emitEvent: false }
      );
      this.cd.detectChanges();
    }

    if (this.modificaModalData.rettifica?.dataDa) {
      this.dataDa.patchValue(
        this.dateConversionService.convertDateToISO(this.modificaModalData.rettifica?.dataDa),
        { emitEvent: false }
      );
      this.cd.detectChanges();
    }

    if (this.modificaModalData.rettifica?.codiceCommessaSorgente) {
      this.codiceCommessaSorgente.patchValue(
        this.commesseErrate.find((c) => c.id === this.modificaModalData.rettifica?.codiceCommessaSorgente) ?? null,
        { emitEvent: true }
      );
      this.cd.detectChanges();
    }

    if (this.modificaModalData.rettifica?.codiceCommessaDestinazione) {
      this.codiceCommessaDestinazione.patchValue(
        this.commesse.find((c) => c.id === this.modificaModalData.rettifica?.codiceCommessaDestinazione) ??
          null,
        { emitEvent: true }
      );
      this.cd.detectChanges();

      if (this.modificaModalData.rettifica?.codiceInterventoDestinazione) {
        this.loaderService.show();
        this.subscriptions.add(
          this.tipologicheService
          .getInterventi(this.modificaModalData.rettifica?.codiceCommessaDestinazione.toString())
          .pipe(
            switchMap((interventi: tipologicaInterface[]) => {
              this.interventiList = interventi;
              return this.tipologicheService.getIsCommessaTariffa(
                this.modificaModalData.rettifica!.codiceCommessaDestinazione!.toString()
              );
            }
            ),
            switchMap((res) => {
              this.isCommessaTariffa = res.isCommessaATariffa;
              return this.tipologicheService.getRuoliTagetikRev({
                matricola: this.matricola,
                codiceCommessaDestinazione: this.modificaModalData.rettifica!.codiceCommessaDestinazione!.toString(),
                codiceInterventoDestinazione: this.modificaModalData.rettifica!.codiceInterventoDestinazione!.toString(),
              });
            })
          )
          .subscribe((res) => {
            this.ruoliList = res;
            this.intervento.patchValue(
              this.interventiList.find(
                (i) => i.id === this.modificaModalData.rettifica?.codiceInterventoDestinazione
              ) ?? null,
              { emitEvent: false }
            );
            this.cd.detectChanges();
            if (this.isCommessaTariffa) {
              this.ruolo.patchValue(
                this.ruoliList.find(
                  (r) => r.descrizione === this.modificaModalData.rettifica?.ruoloTagetik
                )?.id ?? null,
                { emitEvent: false }
              );
              this.ruolo.setValidators([Validators.required]);
              this.ruolo.enable({ emitEvent: false });
              this.ruolo.updateValueAndValidity();
              this.cd.detectChanges();
            } else {
              this.ruolo.disable({ emitEvent: false });
            }
            this.loaderService.hide();
          })
        );
      }
    }
  }

  private onValueChanges() {
    this.subscriptions.add(
      this.dataDa.valueChanges.subscribe((date) => {
        this.dataA.patchValue(date);
      })
    );

    this.subscriptions.add(
      this.codiceCommessaSorgente.valueChanges.subscribe((commessa) => {
        this.filteredCommesse = this.commesse.filter(
          (c) => c.id !== commessa?.id
        );
      })
    );

    this.subscriptions.add(
      this.codiceCommessaDestinazione.valueChanges.subscribe((commessa) => {
      // this.filteredCommesseErrate = this.commesseErrate.filter(
      //   (c) => c.id !== commessa?.id
      // );
      this.intervento.setValue(null);
      this.ruolo.setValue(null);
      this.ruolo.setValidators([]);
      this.ruolo.updateValueAndValidity();
      if (!commessa?.id) {
        this.interventiList = [];
        return;
      }
      this.tipologicheService.getIsCommessaTariffa(commessa.id.toString()).subscribe((res) => {
        this.isCommessaTariffa = res.isCommessaATariffa;
      });
      this.tipologicheService
        .getInterventi(commessa.id.toString())
        .subscribe((interventi: tipologicaInterface[]) => {
        this.interventiList = interventi;
        });
      })
    );

    this.subscriptions.add(
      this.intervento.valueChanges.subscribe((value) => {
        const codiceInterventoDestinazione = value?.id?.toString();
        if (codiceInterventoDestinazione && this.isCommessaTariffa) {
          const codiceCommessaDestinazione = this.commesse.find(
            (c) => c.id === this.codiceCommessaDestinazione.value?.id
          )?.id as string;
          const dataToSend = {
            matricola: this.matricola,
            codiceCommessaDestinazione: codiceCommessaDestinazione,
            codiceInterventoDestinazione: codiceInterventoDestinazione,
          };
          this.tipologicheService
            .getRuoliTagetikRev(dataToSend)
            .subscribe((ruoli: any) => {
              this.ruoliList = ruoli;
              this.ruolo.setValue(null);
              this.ruolo.enable({ emitEvent: false });
              this.ruolo.setValidators([Validators.required]);
              this.ruolo.updateValueAndValidity();
              const selectedRuolo = ruoli.find(
                (ruolo: tipologicaInterface) => ruolo.isSelezionato === true
              );
              if (selectedRuolo) {
                this.ruolo.setValue(selectedRuolo.id);
              }
            });
        }
      })
    );
  }

  isRequired(controlName: string): boolean {
    const control = this.form.get(controlName);
    return control?.validator?.({} as any)?.['required'];
  }

  confirm() {
    const returnValue: Rettifica = { ...this.form.getRawValue() };

    returnValue.matricola = this.matricola;
    returnValue.dataA = this.dateConversionService.convertDateToDDMMYYYY(
      this.dataA.value
    );

    returnValue.dataDa = this.dateConversionService.convertDateToDDMMYYYY(
      this.dataDa.value
    );

    returnValue.codiceCommessaDestinazione = this.filteredCommesse.find(
      (c) => c.id === this.codiceCommessaDestinazione.value?.id
    )?.id as string;

    returnValue.codiceCommessaSorgente = this.commesseErrate.find(
      (c) => c.id === this.codiceCommessaSorgente.value?.id
    )?.id as string;

    returnValue.codiceInterventoDestinazione = this.interventiList.find(
      (i) => i.id === this.intervento.value?.id
    )?.id as string;

    returnValue.ruoloTagetik = this.ruoliList.find(
      (r) => r.id === this.ruolo.value
    )?.descrizione as string;

    this.loaderService.show();
    this.subscriptions.add(
      this.rettificheService.postModifica(returnValue).subscribe({
        next: () => this.loaderService.hide(),
        error: () => {
          this.loaderService.hide();
          // this.dialogRef.close();
        },
        complete: () => this.dialogRef.close(returnValue),
      })
    );
  }

  reject() {
    this.dialogRef.close();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
